import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route } from 'react-router-dom';
// 2.0.0: SearchBar 已移除（搜索UI删除）
import { RestaurantCard } from './shared/components/RestaurantCard';
import { QueryHistorySidebar } from './features/history/components/QueryHistorySidebar';
import { AuthModal } from './features/account/components/AuthModal';
import { ChatInterface } from './features/chat/components/ChatInterface';
import { SharePage } from './features/share/pages/SharePage';
import { MyPage } from './features/account/pages/MyPage';
import { FavoritesPage } from './features/favorites/pages/FavoritesPage';
// 1.3.44: 测试页面
import { TestXMarkdown } from './pages/TestXMarkdown';
import { UpdateModal } from './shared/components/UpdateModal';
import { useRestaurantStore } from './features/search/store/restaurantStore';
import { useChatStore } from './features/chat/store/chatStore';
// 2.0.0: getRestaurantRecommendations 已随搜索模式移除
import type { QueryHistoryItem } from './features/history/types/queryHistory';
import type { Restaurant } from './features/search/types/restaurant';
import { motion, AnimatePresence } from 'framer-motion';
// 2.0.0: 移除 ChefHat, Search, Globe（搜索UI已删除，语言切换改为纯图标）
import { History, X, MessageSquare, MailOpen } from 'lucide-react';
// 2.0.0: 主题切换与语言切换（简洁按钮 + 下拉）
import { ThemeToggle } from './shared/components/ThemeToggle';
import { LanguageSwitcher } from './shared/components/LanguageSwitcher';
import { getCurrentUser, onAuthStateChange, migrateAnonymousQueries, getUserEmail } from './shared/services/authService';
import { processPendingFavorites, addFavorite, removeFavorite, isFavorite, addPendingFavorite, getFavorites } from './shared/services/favoriteService';
import { getAnonymousUserId } from './shared/lib/supabase';
import type { User } from '@supabase/supabase-js';
import { getUpdateInfo, type UpdateInfo } from './shared/services/updateService';
import { getServiceIntro, type ServiceIntroInfo } from './shared/services/serviceIntroService';
import './i18n';
import { safeStorageGet, safeStorageSet } from './shared/utils/safeStorage';
// 2.0.0: 主题系统
import { useThemeStore } from './shared/stores/themeStore';

function App() {
  const { t, i18n } = useTranslation();
  // 2.0.0: 仅保留收藏功能需要的字段（搜索UI相关的 error/dataSource/apiError/setLoading 已不再使用）
  const { restaurants, loadHistory } = useRestaurantStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // For mobile toggle
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [hasMigratedQueries, setHasMigratedQueries] = useState(false);
  const [migrationNotification, setMigrationNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // 2.0.0: viewMode 已移除（始终为 chat 模式）
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  // 1.3.45: 服务介绍相关状态
  const [serviceIntroInfo, setServiceIntroInfo] = useState<ServiceIntroInfo | null>(null);
  const [isLoadingIntro, setIsLoadingIntro] = useState(false);
  const [showServiceIntro, setShowServiceIntro] = useState(false);
  const lastScrollYRef = useRef(0);
  const mainContentRef = useRef<HTMLElement>(null);
  // 2.0.0: Header ref — 动态测量高度，设置 CSS 变量供侧边栏定位
  const headerRef = useRef<HTMLElement>(null);
  // 2.0.0: searchSectionRef 已移除
  const limitModalShownRef = useRef(false); // Track if limit modal has been shown
  // 1.2.15: 收藏功能状态
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});

  // 2.0.0: 初始化主题系统（读取 localStorage + 监听系统偏好）
  useEffect(() => {
    const { init } = useThemeStore.getState();
    init();
  }, []);

  // 2.0.0: 动态测量 Header 高度，写入 CSS 变量供侧边栏使用
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-h', `${h}px`);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // 1.3.45: Check for service intro and updates on mount
  useEffect(() => {
    const checkForServiceIntroAndUpdates = async () => {
      try {
        // First check if user has seen service intro
        const hasSeenServiceIntro = safeStorageGet('has_seen_service_intro') === 'true';
        
        // 1.3.48: 开发环境调试日志
        if (import.meta.env.DEV) {
          console.log('[弹窗逻辑] 检查状态:', {
            hasSeenServiceIntro,
            储存值: safeStorageGet('has_seen_service_intro'),
            版本标记: safeStorageGet('last_seen_update_version')
          });
        }
        
        if (!hasSeenServiceIntro) {
          // Load service intro for first-time users
          setIsLoadingIntro(true);
          const introInfo = await getServiceIntro(i18n.language);
          
          if (introInfo) {
            setServiceIntroInfo(introInfo);
            setShowServiceIntro(true);
            setShowUpdateModal(true);
            if (import.meta.env.DEV) {
              console.log('[弹窗逻辑] 首次用户 → 显示服务介绍');
            }
          }
          setIsLoadingIntro(false);
        }
        
        // Then check for updates
        setIsLoadingUpdate(true);
        const info = await getUpdateInfo(i18n.language);
        
        if (info) {
          // Check if user has seen this version
          const lastSeenVersion = safeStorageGet('last_seen_update_version');
          
          // 1.3.48: 开发环境调试日志
          if (import.meta.env.DEV) {
            console.log('[弹窗逻辑] 版本检查:', {
              当前版本: info.version,
              已读版本: lastSeenVersion,
              是否显示: lastSeenVersion !== info.version && hasSeenServiceIntro
            });
          }
          
          // Show modal if version is different or not seen before
          // If showing service intro, update info will be shown after intro
          if (lastSeenVersion !== info.version) {
            setUpdateInfo(info);
            // Only show modal if not already showing (for service intro)
            if (hasSeenServiceIntro) {
              setShowUpdateModal(true);
              if (import.meta.env.DEV) {
                console.log('[弹窗逻辑] 有新版本 → 显示更新弹窗');
              }
            }
          }
        }
      } catch (error) {
        // Silently fail - don't show modal if API fails
        if (import.meta.env.DEV) {
          console.error('Failed to check for service intro and updates:', error);
        }
      } finally {
        setIsLoadingUpdate(false);
      }
    };

    checkForServiceIntroAndUpdates();
  }, [i18n.language]);

  // Handle scroll for header collapse
  const handleScroll = useCallback((scrollTop: number, scrollInfo?: { isNearBottom: boolean; isAtBottom: boolean; scrollHeight: number; clientHeight: number; distanceFromBottom: number }) => {
    // Only apply on mobile (below md breakpoint)
    if (window.innerWidth >= 768) {
      return;
    }

    const scrollThreshold = 100;
    const lastScrollY = lastScrollYRef.current;
    const scrollDelta = Math.abs(scrollTop - lastScrollY);

    // Determine scroll direction
    const isScrollingUp = scrollTop < lastScrollY;
    const isScrollingDown = scrollTop > lastScrollY;

    // Debug logging (only in development)
    if (import.meta.env.DEV) {
      console.log('Scroll:', { scrollTop, lastScrollY, isScrollingDown, isScrollingUp, threshold: scrollThreshold, scrollInfo, scrollDelta });
    }

    // Handle Safari bounce effect at bottom: completely ignore upward scrolls when at bottom
    // This prevents header from expanding when Safari bounces at the bottom
    if (scrollInfo?.isAtBottom && isScrollingUp) {
      // When at bottom, ignore all upward movements (Safari bounce)
      if (import.meta.env.DEV) {
        console.log('Ignoring Safari bounce effect at bottom (at bottom):', { scrollTop, lastScrollY, scrollDelta, distanceFromBottom: scrollInfo.distanceFromBottom });
      }
      // Don't update lastScrollYRef to prevent state change
      return;
    }

    // Also ignore small upward scrolls when near bottom (within 100px)
    if (scrollInfo?.isNearBottom && isScrollingUp && scrollDelta < 50) {
      // Small upward movement near bottom is likely Safari bounce - ignore it
      if (import.meta.env.DEV) {
        console.log('Ignoring Safari bounce effect at bottom (near bottom):', { scrollTop, lastScrollY, scrollDelta, distanceFromBottom: scrollInfo.distanceFromBottom });
      }
      // Don't update lastScrollYRef to prevent state change
      return;
    }

    if (isScrollingDown && scrollTop > scrollThreshold) {
      // Scrolling down and past threshold - collapse header
      setIsHeaderCollapsed(true);
    } else if (isScrollingUp || scrollTop <= scrollThreshold) {
      // Scrolling up or near top - expand header
      // But only if we're not at/near the bottom (already handled above)
      setIsHeaderCollapsed(false);
    }

    lastScrollYRef.current = scrollTop;
  }, []);

  // Ensure header is expanded on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsHeaderCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 2.0.0: 搜索模式相关的 scroll/visibility effects 已移除

  // 2.0.0: 搜索模式 scroll listener 已移除

  // Listen to auth state changes and migrate queries when user logs in
  useEffect(() => {
    let previousUser: User | null = null;
    let migrationDone = false;
    // P1 修复：追踪 setTimeout ID，组件卸载时清理，防止内存泄漏
    const timerIds: ReturnType<typeof setTimeout>[] = [];

    // Get initial user
    getCurrentUser().then((currentUser) => {
      previousUser = currentUser;
      setUser(currentUser);
      
      // If user is already logged in on mount, don't migrate (they might have already migrated)
      if (currentUser) {
        migrationDone = true;
        setHasMigratedQueries(true);
      }
    });

    // Subscribe to auth changes
    const { unsubscribe } = onAuthStateChange(async (currentUser) => {
      const wasLoggedOut = !previousUser && currentUser;
      const wasLoggedIn = previousUser && !currentUser;

      // If user just logged in (was null, now not null), migrate anonymous queries
      // This handles both login and signup scenarios
      if (wasLoggedOut && !migrationDone) {
        try {
          const anonymousUserId = getAnonymousUserId();
          if (anonymousUserId) {
            // Migration in progress (logged via logger in service)
            const result = await migrateAnonymousQueries(anonymousUserId, currentUser!.id);
            if (result.success) {
              const count = result.migratedCount || 0;
              if (count > 0) {
                // Migration successful (logged via logger in service)
                // Show success notification
                setMigrationNotification({
                  show: true,
                  message: `已成功迁移 ${count} 条查询历史`,
                  type: 'success',
                });
                // P1 修复：自动隐藏通知，使用 timerIds 追踪以便卸载时清理
                timerIds.push(setTimeout(() => {
                  setMigrationNotification(prev => prev ? { ...prev, show: false } : null);
                }, 5000));
              }
              // No queries to migrate - silently handle (logged via logger in service)
              migrationDone = true;
              setHasMigratedQueries(true);
              // Reload history after migration
              loadHistory();
              setSidebarRefreshTrigger(prev => prev + 1);
              
              // 1.2.15: Process pending favorites after login
              try {
                const favoritesResult = await processPendingFavorites();
                if (favoritesResult.processed > 0 && import.meta.env.DEV) {
                  console.log(`✅ Processed ${favoritesResult.processed} pending favorites after login`);
                }
              } catch (error) {
                // Silently handle errors - favorites processing is not critical
                if (import.meta.env.DEV) {
                  console.error('Error processing pending favorites:', error);
                }
              }
            } else {
              // Migration failed (error logged via logger in service)
              // Show error notification
              setMigrationNotification({
                show: true,
                message: result.error?.message || '迁移查询历史失败，请稍后重试',
                type: 'error',
              });
              timerIds.push(setTimeout(() => {
                setMigrationNotification(prev => prev ? { ...prev, show: false } : null);
              }, 5000));
              // Still mark as done to prevent retry loops, but log the error
              migrationDone = true;
            }
          } else {
            // No anonymous user ID (logged via logger in service)
            migrationDone = true;
          }
        } catch (error) {
          // Exception during migration (error logged via logger in service)
          // Mark as done to prevent retry loops
          migrationDone = true;
        }
      }

      // If user logged out, reset migration flag
      if (wasLoggedIn) {
        migrationDone = false;
        setHasMigratedQueries(false);
        // Reload history after logout
        loadHistory();
        setSidebarRefreshTrigger(prev => prev + 1);
      }

      previousUser = currentUser;
      setUser(currentUser);
    });

    return () => {
      unsubscribe();
      // P1 修复：清理所有待执行的定时器，避免组件卸载后更新状态
      timerIds.forEach(clearTimeout);
    };
  }, [loadHistory]);

  // 2.0.0: handleSearch 已移除（搜索UI已删除）

  // 2.0.0: handleSelectQuery 已移除（搜索UI已删除）

  // 2.0.0: 历史项选择 — 仅支持聊天模式（搜索模式已删除）
  const handleSelectHistoryItem = async (item: QueryHistoryItem) => {
    // 加载对话历史
    const { getConversationWithMessages, rowToChatMessage } = await import('./shared/services/conversationService');
    const conversation = await getConversationWithMessages(item.id);
    
    if (conversation && conversation.messages.length > 0) {
      const chatMessages = conversation.messages.map(rowToChatMessage);
      const { loadConversationFromHistory, setCurrentConversationId } = useChatStore.getState();
      loadConversationFromHistory(chatMessages);
      setCurrentConversationId(conversation.id);
    } else {
      // 兼容旧数据: 尝试从 query_history 表加载
      const { getQueryHistoryWithSnapshot } = await import('./shared/services/queryHistoryService');
      const legacy = await getQueryHistoryWithSnapshot(item.id);
      if (legacy?.conversation_messages && legacy.conversation_messages.length > 0) {
        const { loadConversationFromHistory, setCurrentConversationId } = useChatStore.getState();
        loadConversationFromHistory(legacy.conversation_messages);
        setCurrentConversationId(legacy.id);
      } else {
        useChatStore.getState().clearMessages();
      }
    }
    
    // 刷新侧边栏
    setSidebarRefreshTrigger(prev => prev + 1);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleAuthSuccess = async () => {
    // Close auth modal and refresh history
    setIsAuthModalOpen(false);
    loadHistory();
    setSidebarRefreshTrigger(prev => prev + 1);
    
    // 1.2.15: Process pending favorites after login
    try {
      const favoritesResult = await processPendingFavorites();
      if (favoritesResult.processed > 0 && import.meta.env.DEV) {
        console.log(`✅ Processed ${favoritesResult.processed} pending favorites after login`);
      }
    } catch (error) {
      // Silently handle errors - favorites processing is not critical
      if (import.meta.env.DEV) {
        console.error('Error processing pending favorites:', error);
      }
    }
  };

  const handleAuthRequest = () => {
    setIsAuthModalOpen(true);
  };

  // 1.2.16: 加载收藏状态 - 优化性能，使用批量查询和缓存
  const favoriteStatusCacheRef = useRef<{ restaurantIds: Set<string>; status: Record<string, boolean>; timestamp: number } | null>(null);
  const CACHE_TTL = 10000; // 10秒缓存时间
  
  // 1.2.16: 提取 loadFavoriteStatus 函数，支持强制刷新参数（需要在 handleFavoriteClick 之前定义）
  const loadFavoriteStatus = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setFavoriteStatus({});
      favoriteStatusCacheRef.current = null;
      return;
    }

    if (restaurants.length === 0) {
      setFavoriteStatus({});
      favoriteStatusCacheRef.current = null;
      return;
    }

    // 获取所有餐厅ID
    const restaurantIds = new Set(restaurants.map(r => r.id));

    // 1.2.16: 检查缓存 - 如果强制刷新或缓存无效，跳过缓存
    const now = Date.now();
    const cache = favoriteStatusCacheRef.current;
    const restaurantIdsStr = Array.from(restaurantIds).sort().join(',');
    const cachedIdsStr = cache ? Array.from(cache.restaurantIds).sort().join(',') : '';
    
    // 如果不强制刷新且缓存有效，使用缓存
    if (!forceRefresh && cache && restaurantIdsStr === cachedIdsStr && (now - cache.timestamp) < CACHE_TTL) {
      setFavoriteStatus(cache.status);
      return;
    }

    // 1.2.16: 优化性能 - 先批量获取所有收藏，然后在内存中匹配
    // 将 N 个查询优化为 1 个查询
    const status: Record<string, boolean> = {};
    try {
      const favorites = await getFavorites();
      const favoriteIds = new Set(favorites.map(fav => fav.id));
      
      // 在内存中检查每个餐厅是否已收藏
      restaurants.forEach(restaurant => {
        status[restaurant.id] = favoriteIds.has(restaurant.id);
      });
      
      // 更新缓存
      favoriteStatusCacheRef.current = {
        restaurantIds: new Set(restaurantIds),
        status,
        timestamp: now
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading favorites:', error);
      }
      // 如果获取收藏失败，将所有状态设为 false
      restaurants.forEach(restaurant => {
        status[restaurant.id] = false;
      });
    }
    setFavoriteStatus(status);
  }, [user, restaurants]);
  
  // 1.2.16: 处理收藏点击 - 使用 useCallback 优化性能，修复缓存同步问题
  const handleFavoriteClick = useCallback(async (restaurant: Restaurant) => {
    const currentUser = await getCurrentUser();
    
    if (!currentUser) {
      // 匿名用户：添加到待收藏列表并打开登录弹窗
      addPendingFavorite(restaurant);
      setIsAuthModalOpen(true);
      return;
    }

    // 登录用户：切换收藏状态
    const isCurrentlyFavorite = favoriteStatus[restaurant.id] || false;
    const newFavoriteStatus = !isCurrentlyFavorite;
    
    // 1.2.16: 乐观更新 - 立即更新本地状态以提供即时反馈
    setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: newFavoriteStatus }));
    
    try {
      if (isCurrentlyFavorite) {
        // 取消收藏
        const result = await removeFavorite(restaurant.id);
        if (result.success) {
          // 1.2.16: 强制重新加载收藏状态以确保数据同步
          await loadFavoriteStatus(true);
          // 1.2.15: 刷新侧边栏收藏数量
          setSidebarRefreshTrigger(prev => prev + 1);
        } else {
          // 如果操作失败，恢复原状态
          setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
        }
      } else {
        // 添加收藏
        const result = await addFavorite(restaurant);
        if (result.success) {
          // 1.2.16: 强制重新加载收藏状态以确保数据同步
          await loadFavoriteStatus(true);
          // 1.2.15: 刷新侧边栏收藏数量
          setSidebarRefreshTrigger(prev => prev + 1);
        } else {
          // 如果操作失败，恢复原状态
          setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error toggling favorite:', error);
      }
      // 如果出错，恢复原状态
      setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
    }
  }, [favoriteStatus, loadFavoriteStatus]);
  
  useEffect(() => {
    if (restaurants.length > 0) {
      loadFavoriteStatus(false);
    }
  }, [restaurants, user, loadFavoriteStatus]);

  // 2.0.0: 新对话（不再需要切换 viewMode）
  const handleNewConversation = () => {
    useChatStore.getState().clearMessages();
    useChatStore.getState().setCurrentConversationId(null);
  };

  // Handle update modal confirm
  const handleUpdateConfirm = () => {
    // 1.3.48: 关闭弹窗时同时写入两个"已读"标记，避免刷新后再次触发
    // - 服务介绍：关闭也视为已看过（满足"首次打开才弹"）
    if (showServiceIntro) {
      safeStorageSet('has_seen_service_intro', 'true');
      setShowServiceIntro(false);
      if (import.meta.env.DEV) {
        console.log('[弹窗逻辑] 关闭时写入 has_seen_service_intro = true');
      }
    }
    // - 更新：无论当前在哪一页，只要有版本信息就记录（避免刷新后再弹更新页）
    if (updateInfo?.version) {
      safeStorageSet('last_seen_update_version', updateInfo.version);
      if (import.meta.env.DEV) {
        console.log('[弹窗逻辑] 关闭时写入 last_seen_update_version =', updateInfo.version);
      }
    }
    setShowUpdateModal(false);
  };

  return (
    <Routes>
      {/* Share Page Route - No sidebar or header */}
      <Route path="/share/:shareToken" element={<SharePage />} />
      
      {/* My Page 和 Favorites 移入主布局（保留 sidebar + header） */}
      
      {/* 1.3.44: Test Page Route - Development only */}
      {import.meta.env.DEV && (
        <Route path="/test-xmarkdown" element={<TestXMarkdown />} />
      )}
      
      {/* Main App Route */}
      <Route path="*" element={
        <div className="min-h-screen h-[100dvh] flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>

      {/* Limit Modal */}
      <AnimatePresence>
        {showLimitModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setShowLimitModal(false); limitModalShownRef.current = false; }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[60] flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-black border border-gray-800 rounded-xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <div className="mb-6 space-y-2">
                    <p className="text-white text-base leading-relaxed">{t('limitModalMessage')}</p>
                    <p className="text-gray-300 text-sm leading-relaxed">{t('limitModalSuggestion')}</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowLimitModal(false); limitModalShownRef.current = false; }}
                      className="flex-1 px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200"
                    >{t('limitModalCancel')}</button>
                    <button
                      onClick={() => { setShowLimitModal(false); limitModalShownRef.current = false; setIsAuthModalOpen(true); }}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200"
                    >{t('limitModalRegister')}</button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onSuccess={handleAuthSuccess} />

      {/* Update Modal */}
      <UpdateModal
        isOpen={showUpdateModal} onClose={handleUpdateConfirm} updateInfo={updateInfo}
        isLoading={isLoadingUpdate} serviceIntroInfo={serviceIntroInfo}
        isLoadingIntro={isLoadingIntro} showServiceIntro={showServiceIntro}
      />

      {/* ===== Header — 贯穿全屏宽度，位于最顶层 ===== */}
      <header ref={headerRef} className="flex-shrink-0 bg-white dark:bg-gray-950 border-b border-orange-200 dark:border-gray-800 z-50">
        <div className="w-full px-3 xs:px-4 sm:px-6 py-2 xs:py-2.5">
          <div className="flex items-center justify-between">
            {/* 左侧: Logo + 标题 */}
            <div className="flex items-center gap-2 xs:gap-3">
              {/* 移动端侧边栏切换 */}
              <button
                onClick={toggleSidebar}
                className="md:hidden p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <History className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              {/* Ai Logo */}
              <div className="p-1.5 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-sm leading-none">
                  <span>A</span><span className="text-orange-200">i</span>
                </span>
              </div>
              <h1 className="text-base xs:text-lg font-bold text-gray-900 dark:text-white">{t('appTitle')}</h1>
            </div>

            {/* 右侧: Chat / Theme / Language / Feedback */}
            <div className="flex items-center gap-2 xs:gap-3">
              {/* Chat 标识 */}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-orange-600 border-b-2 border-orange-500">
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">{t('chatMode', 'Chat')}</span>
              </div>
              <ThemeToggle />
              <LanguageSwitcher />
              {/* P1 修复：反馈表单 URL 从环境变量读取，不再硬编码 */}
              <a
                href={import.meta.env.VITE_FEEDBACK_FORM_URL || '#'}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 p-1.5 text-gray-500 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                title={t('feedback', '意见')} aria-label={t('feedback', '意见')}
              >
                <MailOpen className="w-4 h-4" />
                <span className="text-xs sm:text-sm hidden xs:inline">{t('feedback', 'Feedback')}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ===== Body: Sidebar + Main Content（横向排列，位于 Header 下方） ===== */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar — 起始于 Header 下方 */}
        <QueryHistorySidebar
          onSelectHistoryItem={handleSelectHistoryItem}
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          refreshTrigger={sidebarRefreshTrigger}
          onAuthRequest={handleAuthRequest}
          isCollapsed={isSidebarCollapsed}
          onCollapsedChange={setIsSidebarCollapsed}
          onNewConversation={handleNewConversation}
        />

        {/* Main Content Area */}
        <div className={`flex-1 flex flex-col min-w-0 min-h-0 transition-all duration-300 ${
          isSidebarCollapsed ? 'md:ml-[60px]' : 'md:ml-[300px]'
        }`}>
          {/* Migration Notification */}
          {migrationNotification?.show && (
            <motion.div
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className={`fixed top-14 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
                migrationNotification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                : migrationNotification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800'
                : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
            >
              <div className="flex-1"><p className="text-sm font-medium">{migrationNotification.message}</p></div>
              <button onClick={() => setMigrationNotification(prev => prev ? { ...prev, show: false } : null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Main Content — 根据路由显示不同页面，均保留 sidebar + header */}
          <main ref={mainContentRef} className="flex-1 flex flex-col min-h-0 overflow-hidden px-0">
            <Routes>
              <Route path="/favorites" element={
                <div className="flex-1 overflow-y-auto">
                  <FavoritesPage />
                </div>
              } />
              <Route path="/my-page" element={
                <div className="flex-1 overflow-y-auto">
                  <MyPage />
                </div>
              } />
              <Route path="*" element={
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <ChatInterface
                    language={i18n.language}
                    isSidebarCollapsed={isSidebarCollapsed}
                    onScroll={handleScroll}
                    onConversationSaved={() => setSidebarRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              } />
            </Routes>
          </main>
        </div>
      </div>
    </div>
      } />
    </Routes>
  );
}

export default App;
